import React, { useMemo, useState } from 'react';
import { Box, Typography, Paper, Stack, TextField, MenuItem, Pagination, Chip, Button, Alert } from '@mui/material';
import { useAdminUsers, useUpdateAdminUser, useCreateAdminUser, CreateUserPayload } from '../../../services/adminUsers';
import { UsersTable } from './UsersTable';
import { EditUserDialog } from './EditUserDialog';
import { CreateUserDialog } from './CreateUserDialog';
import { useAuth } from '../../../components/auth/AuthProvider';

const ROLE_FILTERS = ['', 'employee', 'manager', 'finance', 'admin', 'super_admin'] as const;

export const UsersSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const AUTH_PROVIDER = (process.env.REACT_APP_AUTH_PROVIDER || 'AZURE_AD').toUpperCase();
  const isDbProvider = AUTH_PROVIDER === 'DB';
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(20);
  const [search, setSearch] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [approver, setApprover] = useState<string>(''); // '', 'true', 'false'

  const params = useMemo(() => ({ page, size, search: search.trim() || undefined, role: role || undefined, approver: approver === '' ? undefined : approver === 'true' }), [page, size, search, role, approver]);
  const { data, isLoading, isFetching, isError, error } = useAdminUsers(params) as any;
  const updateMutation = useUpdateAdminUser();
  const createMutation = useCreateAdminUser();

  const [editing, setEditing] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);

  const onEdit = (user: any) => {
    setEditing(user);
    setDialogOpen(true);
  };

  const onSave = async (payload: any) => {
    if (!editing) return;
    await updateMutation.mutateAsync({ userId: editing.id, payload });
  };

  const onCreate = async (payload: CreateUserPayload) => {
    await createMutation.mutateAsync(payload);
  };

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h4" fontWeight={700}>User Management</Typography>
        <Typography variant="body1" color="text.secondary">Admins can manage user roles and approval settings.</Typography>
      </Box>

      <Paper className="p-4">
        {isError && (
          <Box mb={2}>
            <Alert severity="error">
              {error?.userMessage || (error as any)?.message || 'Failed to load users'}
            </Alert>
          </Box>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Search name or email"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            size="small"
            fullWidth
          />
          <TextField
            label="Role"
            select
            size="small"
            value={role}
            onChange={(e) => { setRole(e.target.value); setPage(1); }}
            sx={{ minWidth: 180 }}
          >
            {ROLE_FILTERS.map((r) => (
              <MenuItem key={r || 'any'} value={r}>{r || 'Any'}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Approver"
            select
            size="small"
            value={approver}
            onChange={(e) => { setApprover(e.target.value); setPage(1); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
          <TextField
            label="Page size"
            select
            size="small"
            value={size}
            onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
            sx={{ minWidth: 140 }}
          >
            {[10, 20, 50, 100].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <Chip label={isFetching ? 'Refreshing…' : isLoading ? 'Loading…' : 'Ready'} color={isFetching || isLoading ? 'default' : 'success'} variant="outlined" />
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Users</Typography>
        {(user?.roles || []).includes('super_admin') && isDbProvider && (
          <Button variant="contained" onClick={() => setCreateOpen(true)}>Add User</Button>
        )}
      </Stack>

      <UsersTable users={items} onEdit={onEdit} loading={isLoading || isFetching} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Page {page} of {totalPages} — {data?.total ?? 0} users
        </Typography>
        <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
      </Stack>

      <EditUserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        user={editing}
        onSave={onSave}
      />

      {isDbProvider && (
        <CreateUserDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={onCreate}
          isSubmitting={createMutation.isLoading}
          error={createMutation.isError ? (createMutation.error as any)?.userMessage || (createMutation.error as any)?.message || 'Failed to create user' : null}
        />
      )}
    </Stack>
  );
};

export default UsersSettingsPage;
